import { computed } from 'vue'
import { useStore } from 'vuex'

/**
 * =====================================================================================
 * PERMISOS POR ROL - Definición centralizada en el frontend
 * =====================================================================================
 *
 * En lugar de cargar permisos desde el backend, definimos los permisos
 * de cada rol directamente aquí. Esto simplifica el backend y centraliza
 * la lógica de autorización en el frontend.
 *
 * ROLES DISPONIBLES:
 * - SuperAdmin: Acceso total a todo el sistema
 * - EmisorAdmin: Todo excepto gestión de emisores (solo ve su emisor)
 * - GerenteSucursal: Gestión de su sucursal, emisión, reportes
 * - Cajero: Emisión básica y consultas
 * - Contador: Solo lectura y reportes
 * - Auditor: Solo lectura de todo
 */

// Definición de permisos por rol (exportado para usar en router)
// ALINEADO CON BACKEND: Los permisos corresponden a los [Authorize(Roles=...)] en controllers
//
// REFERENCIA BACKEND (Controllers y sus roles autorizados):
// - DashboardController: SuperAdmin, EmisorAdmin, GerenteSucursal
// - EmisoresController: SuperAdmin (CRUD), EmisorAdmin/GerenteSucursal (solo perfil)
// - UsuariosController: SuperAdmin, EmisorAdmin, GerenteSucursal
// - LotesController: EmisorAdmin, GerenteSucursal, Contador
// - EventosContingenciaController: EmisorAdmin, GerenteSucursal, Contador
// - ExportController: EmisorAdmin, GerenteSucursal, Contador, Auditor
// - ProveedoresController: EmisorAdmin, GerenteSucursal, Contador, Auditor
// - ComprasExternasController: EmisorAdmin, GerenteSucursal, Contador, Auditor
// - InventarioReportesController: EmisorAdmin, GerenteSucursal, Contador, Auditor
// - GastosAdministrativosController: EmisorAdmin, GerenteSucursal, Contador
// - FacturasController: EmisorAdmin, GerenteSucursal, Cajero (NO SuperAdmin)
// - ProductosServiciosController: EmisorAdmin, GerenteSucursal, Cajero (NO SuperAdmin)
// - ReceptoresController: EmisorAdmin, GerenteSucursal, Cajero (NO SuperAdmin)
// - SucursalesController: EmisorAdmin (CRUD), GerenteSucursal/Cajero (solo ver)
//
export const PERMISOS_POR_ROL = {
  // SuperAdmin: Gestión administrativa del sistema
  // Backend incluye SuperAdmin en: Dashboard, Emisores, Usuarios, Cajas, Vendedores
  // Backend NO incluye SuperAdmin en: Facturas, Productos, Receptores, Sucursales,
  // Lotes, Contingencia, Export, Proveedores, Compras, InventarioReportes, GastosAdministrativos
  SuperAdmin: [
    'dashboard.ver', 'dashboard.estadisticas',
    'usuarios.ver', 'usuarios.crear', 'usuarios.editar', 'usuarios.eliminar',
    'emisores.ver', 'emisores.crear', 'emisores.gestionar',
    'pendientes.ver', 'pendientes.enviar',  // DTEs pendientes de envío
    'lotes.ver', 'lotes.crear', 'lotes.enviar',
    'contingencia.ver', 'contingencia.crear',
    'proveedores.ver', 'proveedores.crear', 'proveedores.editar', 'proveedores.eliminar',
    'compras.ver', 'compras.crear', 'compras.confirmar', 'compras.anular',
    'inventario.ver', 'inventario.stock', 'inventario.kardex', 'inventario.movimientos',
    'reportes.ver', 'reportes.exportar',
    'configuracion.ver', 'configuracion.editar',
    'cajas.ver', 'cajas.crear', 'cajas.editar', 'cajas.eliminar',
    'vendedores.ver', 'vendedores.crear', 'vendedores.editar', 'vendedores.asignar',
    'auditoria.ver',
    'suscripciones.ver', 'suscripciones.crear', 'suscripciones.editar', 'suscripciones.eliminar'
    // NO incluye: emision.*, historial.*, clientes.*, productos.*, sucursales.*
  ],

  // EmisorAdmin: Todo excepto gestión de emisores (multi-tenant)
  // Backend incluye EmisorAdmin en todos los controllers excepto Emisores (solo perfil)
  EmisorAdmin: [
    'dashboard.ver', 'dashboard.estadisticas',
    'emision.crear', 'emision.credito_fiscal', 'emision.sujeto_excluido', 'emision.nota_credito', 'emision.nota_debito', 'emision.anular', 'emision.borrador',
    'historial.ver', 'historial.pdf', 'historial.json', 'historial.reenviar',
    'pendientes.ver', 'pendientes.enviar',  // DTEs pendientes de envío
    'lotes.ver', 'lotes.crear', 'lotes.enviar',
    'contingencia.ver', 'contingencia.crear',
    'clientes.ver', 'clientes.crear', 'clientes.editar', 'clientes.eliminar',
    'productos.ver', 'productos.crear', 'productos.editar', 'productos.eliminar',
    'categorias.ver', 'categorias.crear', 'categorias.editar', 'categorias.eliminar',
    'marcas.ver', 'marcas.crear', 'marcas.editar', 'marcas.eliminar',
    'tiposGasto.ver', 'tiposGasto.crear', 'tiposGasto.editar', 'tiposGasto.eliminar',
    'proveedores.ver', 'proveedores.crear', 'proveedores.editar', 'proveedores.eliminar',
    'compras.ver', 'compras.crear', 'compras.confirmar', 'compras.anular',
    'inventario.ver', 'inventario.stock', 'inventario.kardex', 'inventario.movimientos',
    'reportes.ver', 'reportes.exportar',
    'sucursales.ver', 'sucursales.crear', 'sucursales.editar', 'sucursales.eliminar',
    'bodegas.ver', 'bodegas.crear', 'bodegas.editar', 'bodegas.eliminar',
    'usuarios.ver', 'usuarios.crear', 'usuarios.editar', 'usuarios.eliminar',
    'cajas.ver', 'cajas.crear', 'cajas.editar', 'cajas.eliminar',
    'vendedores.ver', 'vendedores.crear', 'vendedores.editar', 'vendedores.asignar',
    'configuracion.ver', 'configuracion.editar',
    'auditoria.ver',
    'mi-suscripcion.ver'
    // NO incluye: emisores.ver, emisores.crear, emisores.gestionar
  ],

  // GerenteSucursal: Operaciones de su sucursal + gestión de usuarios Cajero
  // Backend incluye GerenteSucursal en: Dashboard, Facturas, Productos, Receptores,
  // Lotes, Contingencia, Export, Proveedores, Compras, InventarioReportes, Gastos, Cajas, Vendedores, Usuarios
  // Backend NO incluye GerenteSucursal en: Sucursales (CRUD)
  GerenteSucursal: [
    'dashboard.ver', 'dashboard.estadisticas',
    'emision.crear', 'emision.credito_fiscal', 'emision.sujeto_excluido', 'emision.nota_credito', 'emision.nota_debito', 'emision.anular', 'emision.borrador',
    'historial.ver', 'historial.pdf', 'historial.json', 'historial.reenviar',
    'pendientes.ver', 'pendientes.enviar',  // DTEs pendientes de envío
    'lotes.ver', 'lotes.crear', 'lotes.enviar',
    'contingencia.ver', 'contingencia.crear',
    'clientes.ver', 'clientes.crear', 'clientes.editar', 'clientes.eliminar',
    'productos.ver', 'productos.crear',  // Sin editar ni eliminar
    'categorias.ver', 'categorias.crear',  // Sin editar ni eliminar
    'marcas.ver', 'marcas.crear',  // Sin editar ni eliminar
    'tiposGasto.ver', 'tiposGasto.crear',  // Sin editar ni eliminar
    'proveedores.ver', 'proveedores.crear',  // Sin editar ni eliminar
    'compras.ver', 'compras.crear', 'compras.confirmar',
    'inventario.ver', 'inventario.stock', 'inventario.kardex', 'inventario.movimientos',
    'reportes.ver', 'reportes.exportar',
    'sucursales.ver',
    'bodegas.ver', 'bodegas.crear', 'bodegas.editar', 'bodegas.eliminar',
    'usuarios.ver', 'usuarios.crear', 'usuarios.editar', 'usuarios.eliminar',  // Solo Cajero (filtrado en backend y UI)
    'cajas.ver', 'cajas.crear', 'cajas.editar',  // Sin eliminar
    'vendedores.ver', 'vendedores.crear', 'vendedores.editar', 'vendedores.asignar',
    'configuracion.ver',
    'mi-suscripcion.ver'
    // NO incluye: usuarios.eliminar, sucursales.crear/editar/eliminar, cajas.eliminar
  ],

  // Cajero: Operaciones básicas de punto de venta
  // Backend incluye Cajero en: Facturas, Productos (ver), Receptores (CRUD), Sucursales (ver),
  // Dashboard (cajero), Pendientes, Lotes, Contingencia, ConfiguracionEnvioLote
  Cajero: [
    'emision.crear', 'emision.credito_fiscal', 'emision.sujeto_excluido', 'emision.nota_credito', 'emision.nota_debito', 'emision.borrador',
    'historial.ver', 'historial.pdf', 'historial.json',
    'clientes.ver', 'clientes.crear', 'clientes.editar', 'clientes.eliminar',
    'productos.ver',
    'categorias.ver',  // Solo lectura
    'marcas.ver',  // Solo lectura
    'tiposGasto.ver',  // Solo lectura
    'sucursales.ver',
    'bodegas.ver',
    'inventario.stock',  // Ver stock de productos
    'pendientes.ver', 'pendientes.enviar',  // DTEs pendientes de envío
    'lotes.ver',  // Lotes de envío
    'contingencia.ver', 'contingencia.crear',  // Contingencia
    'mi-suscripcion.ver'
    // NO incluye: proveedores.*, compras.*, inventario.ver, reportes.*, usuarios.*,
    // productos.crear/editar/eliminar, categorias.crear/editar/eliminar, marcas.crear/editar/eliminar,
    // tiposGasto.crear/editar/eliminar
  ],

  // Contador: Solo lectura para contabilidad y reportes
  // Backend incluye Contador en: Export, Proveedores, Compras, InventarioReportes, Gastos,
  // Facturas (lectura + pendientes), Productos (lectura), Lotes, Contingencia
  // Backend NO incluye Contador en: Dashboard, Categorías, Marcas, TiposGasto, Receptores, Usuarios, Sucursales
  Contador: [
    'historial.ver', 'historial.pdf', 'historial.json',  // Facturas - solo lectura
    'pendientes.ver',  // Facturas pendientes - solo lectura
    'lotes.ver',  // Lotes - solo lectura
    'contingencia.ver',  // Contingencia - solo lectura
    'proveedores.ver',
    'compras.ver',
    'productos.ver',  // Catálogo de productos y servicios - solo lectura
    'inventario.ver', 'inventario.stock', 'inventario.kardex',
    'reportes.ver', 'reportes.exportar',
    'mi-suscripcion.ver'
    // NO incluye: dashboard.*, emision.*, clientes.*, categorias.*, marcas.*, tiposGasto.*,
    // usuarios.*, sucursales.*
  ],

  // Auditor: Solo lectura de todo (sin emisión ni escritura)
  // Backend incluye Auditor en GETs de: Dashboard, Facturas, Receptores, Productos, Categorías, Marcas,
  // Proveedores, Compras, InventarioReportes, Gastos, Export, Sucursales, Bodegas, Cajas, Vendedores,
  // Usuarios, Lotes, EventosContingencia
  // Backend NO incluye Auditor en: POST/PUT/DELETE de ningún controller, ni emisión de DTEs
  Auditor: [
    'dashboard.ver', 'dashboard.estadisticas',
    'historial.ver', 'historial.pdf', 'historial.json',
    'pendientes.ver',
    'lotes.ver',
    'contingencia.ver',
    'clientes.ver',
    'productos.ver',
    'categorias.ver',
    'marcas.ver',
    'tiposGasto.ver',
    'proveedores.ver',
    'compras.ver',
    'inventario.ver', 'inventario.stock', 'inventario.kardex', 'inventario.movimientos',
    'reportes.ver', 'reportes.exportar',
    'sucursales.ver',
    'bodegas.ver',
    'usuarios.ver',
    'cajas.ver',
    'vendedores.ver',
    'configuracion.ver',
    'auditoria.ver',
    'mi-suscripcion.ver'
    // NO incluye: emision.*, *.crear, *.editar, *.eliminar, *.confirmar, *.anular, *.enviar, *.reenviar
  ]
}

// Roles que requieren restricción por sucursal (alineado con ScopeHelper.cs del backend)
const ROLES_RESTRINGIDOS_POR_SUCURSAL = ['GerenteSucursal', 'Cajero', 'Contador', 'Auditor']

export function usePermisos() {
  const store = useStore()

  const usuario = computed(() => store.getters['auth/currentUser'])
  const rol = computed(() => usuario.value?.rol || null)

  // Obtener permisos basados en el rol del usuario
  const permisos = computed(() => {
    if (!rol.value) return []
    return PERMISOS_POR_ROL[rol.value] || []
  })

  // =====================================================================================
  // INFORMACIÓN DE SUCURSAL ASIGNADA
  // =====================================================================================
  // Los roles GerenteSucursal, Cajero, Contador y Auditor tienen sucursales asignadas.
  // SuperAdmin y EmisorAdmin pueden acceder a todas las sucursales.

  // ID de la sucursal asignada (null si no tiene restricción)
  const sucursalId = computed(() => usuario.value?.sucursalId || null)

  // IDs de todas las sucursales asignadas (array, para multi-sucursal)
  const sucursalIds = computed(() => usuario.value?.sucursalIds || [])

  // Nombre de la sucursal asignada
  const sucursalNombre = computed(() => usuario.value?.sucursalNombre || null)

  // Indica si el usuario tiene una sucursal fija (exactamente una sucursal asignada)
  const tieneSucursalFija = computed(() => {
    if (sucursalIds.value.length === 1) return true
    return sucursalId.value != null
  })

  // Verifica si el rol actual requiere restricción por sucursal
  const requiereRestriccionSucursal = computed(() =>
    ROLES_RESTRINGIDOS_POR_SUCURSAL.includes(rol.value)
  )

  // Helper para verificar si el usuario puede acceder a una sucursal específica
  const puedeAccederSucursal = (sucursalIdParam) => {
    // Si no requiere restricción (SuperAdmin, EmisorAdmin), puede acceder a cualquiera
    if (!requiereRestriccionSucursal.value) return true
    // Si tiene múltiples sucursales asignadas, verificar en el array
    if (sucursalIds.value.length > 0) return sucursalIds.value.includes(sucursalIdParam)
    // Fallback a sucursal única
    return sucursalId.value === sucursalIdParam
  }

  const tienePermiso = (slug) => {
    if (!slug) return true
    return permisos.value.includes(slug)
  }

  const tieneAlgunPermiso = (slugs) => {
    if (!slugs || slugs.length === 0) return true
    return slugs.some(slug => permisos.value.includes(slug))
  }

  const tieneTodosLosPermisos = (slugs) => {
    if (!slugs || slugs.length === 0) return true
    return slugs.every(slug => permisos.value.includes(slug))
  }

  // Helpers para verificar rol
  const esSuperAdmin = computed(() => rol.value === 'SuperAdmin')
  const esEmisorAdmin = computed(() => rol.value === 'EmisorAdmin')
  const esGerenteSucursal = computed(() => rol.value === 'GerenteSucursal')
  const esCajero = computed(() => rol.value === 'Cajero')
  const esContador = computed(() => rol.value === 'Contador')
  const esAuditor = computed(() => rol.value === 'Auditor')

  // Permisos de Dashboard
  const puedeVerDashboard = computed(() => tienePermiso('dashboard.ver'))
  const puedeVerEstadisticas = computed(() => tienePermiso('dashboard.estadisticas'))

  // Permisos de Emisión
  const puedeCrearFactura = computed(() => tienePermiso('emision.crear'))
  const puedeCrearCreditoFiscal = computed(() => tienePermiso('emision.credito_fiscal'))
  const puedeCrearFSE = computed(() => tienePermiso('emision.sujeto_excluido'))
  const puedeCrearNotaCredito = computed(() => tienePermiso('emision.nota_credito'))
  const puedeCrearNotaDebito = computed(() => tienePermiso('emision.nota_debito'))
  const puedeAnularDTE = computed(() => tienePermiso('emision.anular'))
  const puedeGuardarBorrador = computed(() => tienePermiso('emision.borrador'))

  // Permisos de Historial
  const puedeVerHistorial = computed(() => tienePermiso('historial.ver'))
  const puedeDescargarPDF = computed(() => tienePermiso('historial.pdf'))
  const puedeDescargarJSON = computed(() => tienePermiso('historial.json'))
  const puedeReenviarDTE = computed(() => tienePermiso('historial.reenviar'))

  // Permisos de Clientes
  const puedeVerClientes = computed(() => tienePermiso('clientes.ver'))
  const puedeCrearCliente = computed(() => tienePermiso('clientes.crear'))
  const puedeEditarCliente = computed(() => tienePermiso('clientes.editar'))
  const puedeEliminarCliente = computed(() => tienePermiso('clientes.eliminar'))

  // Permisos de Productos
  const puedeVerProductos = computed(() => tienePermiso('productos.ver'))
  const puedeCrearProducto = computed(() => tienePermiso('productos.crear'))
  const puedeEditarProducto = computed(() => tienePermiso('productos.editar'))
  const puedeEliminarProducto = computed(() => tienePermiso('productos.eliminar'))

  // Permisos de Categorías
  const puedeVerCategorias = computed(() => tienePermiso('categorias.ver'))
  const puedeCrearCategoria = computed(() => tienePermiso('categorias.crear'))
  const puedeEditarCategoria = computed(() => tienePermiso('categorias.editar'))
  const puedeEliminarCategoria = computed(() => tienePermiso('categorias.eliminar'))

  // Permisos de Marcas
  const puedeVerMarcas = computed(() => tienePermiso('marcas.ver'))
  const puedeCrearMarca = computed(() => tienePermiso('marcas.crear'))
  const puedeEditarMarca = computed(() => tienePermiso('marcas.editar'))
  const puedeEliminarMarca = computed(() => tienePermiso('marcas.eliminar'))

  // Permisos de Tipos de Gasto
  const puedeVerTiposGasto = computed(() => tienePermiso('tiposGasto.ver'))

  // Permisos de Proveedores
  const puedeVerProveedores = computed(() => tienePermiso('proveedores.ver'))
  const puedeCrearProveedor = computed(() => tienePermiso('proveedores.crear'))
  const puedeEditarProveedor = computed(() => tienePermiso('proveedores.editar'))
  const puedeEliminarProveedor = computed(() => tienePermiso('proveedores.eliminar'))

  // Permisos de Compras Externas
  const puedeVerCompras = computed(() => tienePermiso('compras.ver'))
  const puedeCrearCompra = computed(() => tienePermiso('compras.crear'))
  const puedeConfirmarCompra = computed(() => tienePermiso('compras.confirmar'))
  const puedeAnularCompra = computed(() => tienePermiso('compras.anular'))

  // Permisos de Reportes
  const puedeVerReportes = computed(() => tienePermiso('reportes.ver'))
  const puedeExportarReportes = computed(() => tienePermiso('reportes.exportar'))

  // Permisos de Sucursales
  const puedeVerSucursales = computed(() => tienePermiso('sucursales.ver'))
  const puedeCrearSucursal = computed(() => tienePermiso('sucursales.crear'))
  const puedeEditarSucursal = computed(() => tienePermiso('sucursales.editar'))
  const puedeEliminarSucursal = computed(() => tienePermiso('sucursales.eliminar'))

  // Permisos de Bodegas
  const puedeVerBodegas = computed(() => tienePermiso('bodegas.ver'))
  const puedeCrearBodega = computed(() => tienePermiso('bodegas.crear'))
  const puedeEditarBodega = computed(() => tienePermiso('bodegas.editar'))
  const puedeEliminarBodega = computed(() => tienePermiso('bodegas.eliminar'))

  // Permisos de Cajas
  const puedeVerCajas = computed(() => tienePermiso('cajas.ver'))
  const puedeCrearCaja = computed(() => tienePermiso('cajas.crear'))
  const puedeEditarCaja = computed(() => tienePermiso('cajas.editar'))
  const puedeEliminarCaja = computed(() => tienePermiso('cajas.eliminar'))

  // Permisos de Vendedores
  const puedeVerVendedores = computed(() => tienePermiso('vendedores.ver'))
  const puedeCrearVendedor = computed(() => tienePermiso('vendedores.crear'))
  const puedeEditarVendedor = computed(() => tienePermiso('vendedores.editar'))
  const puedeAsignarVendedor = computed(() => tienePermiso('vendedores.asignar'))

  // Permisos de Usuarios
  const puedeVerUsuarios = computed(() => tienePermiso('usuarios.ver'))
  const puedeCrearUsuario = computed(() => tienePermiso('usuarios.crear'))
  const puedeEditarUsuario = computed(() => tienePermiso('usuarios.editar'))
  const puedeEliminarUsuario = computed(() => tienePermiso('usuarios.eliminar'))

  // Permisos de Configuración
  const puedeVerConfiguracion = computed(() => tienePermiso('configuracion.ver'))
  const puedeEditarConfiguracion = computed(() => tienePermiso('configuracion.editar'))

  // Permisos de Emisores (SOLO SuperAdmin)
  const puedeVerEmisores = computed(() => tienePermiso('emisores.ver'))
  const puedeGestionarEmisores = computed(() => tienePermiso('emisores.gestionar'))

  // Permisos de Mi Suscripción (todos los roles de emisor)
  const puedeVerMiSuscripcion = computed(() => tienePermiso('mi-suscripcion.ver'))

  // Permisos de Suscripciones (SOLO SuperAdmin)
  const puedeVerSuscripciones = computed(() => tienePermiso('suscripciones.ver'))
  const puedeCrearSuscripcion = computed(() => tienePermiso('suscripciones.crear'))
  const puedeEditarSuscripcion = computed(() => tienePermiso('suscripciones.editar'))
  const puedeEliminarSuscripcion = computed(() => tienePermiso('suscripciones.eliminar'))

  // Permisos de Inventario
  const puedeVerInventario = computed(() => tienePermiso('inventario.ver'))

  // Permisos de Facturas Pendientes de Envío
  const puedeVerPendientes = computed(() => tienePermiso('pendientes.ver'))
  const puedeEnviarPendientes = computed(() => tienePermiso('pendientes.enviar'))

  // Permisos de Lotes
  const puedeVerLotes = computed(() => tienePermiso('lotes.ver'))
  const puedeCrearLote = computed(() => tienePermiso('lotes.crear'))
  const puedeEnviarLote = computed(() => tienePermiso('lotes.enviar'))

  // Permisos de Contingencia
  const puedeVerContingencia = computed(() => tienePermiso('contingencia.ver'))
  const puedeCrearContingencia = computed(() => tienePermiso('contingencia.crear'))
  // Alias para compatibilidad con ContingenciaView.vue
  const puedeActivarContingencia = puedeCrearContingencia

  // Permisos de Auditoría
  const puedeVerAuditoria = computed(() => tienePermiso('auditoria.ver'))

  // Permisos compuestos
  const puedeEmitirDTE = computed(() =>
    tieneAlgunPermiso(['emision.crear', 'emision.credito_fiscal', 'emision.nota_credito', 'emision.nota_debito'])
  )

  const puedeAccederConfiguracion = computed(() =>
    tieneAlgunPermiso([
      'configuracion.ver',
      'configuracion.editar',
      'usuarios.ver',
      'sucursales.ver',
      'emisores.ver',
      'clientes.ver',
      'proveedores.ver'
    ])
  )

  // =====================================================================================
  // HELPERS PARA MOSTRAR/OCULTAR COLUMNA DE ACCIONES EN TABLAS
  // =====================================================================================
  // La columna de acciones solo se muestra si el usuario puede realizar al menos
  // una acción (editar, eliminar, u otra acción específica del módulo)

  // Clientes/Receptores: mostrar si puede editar o eliminar
  const mostrarAccionesClientes = computed(() =>
    tieneAlgunPermiso(['clientes.editar', 'clientes.eliminar'])
  )

  // Productos: mostrar si puede editar o eliminar
  const mostrarAccionesProductos = computed(() =>
    tieneAlgunPermiso(['productos.editar', 'productos.eliminar'])
  )

  // Categorías: mostrar si puede editar o eliminar
  const mostrarAccionesCategorias = computed(() =>
    tieneAlgunPermiso(['categorias.editar', 'categorias.eliminar'])
  )

  // Marcas: mostrar si puede editar o eliminar
  const mostrarAccionesMarcas = computed(() =>
    tieneAlgunPermiso(['marcas.editar', 'marcas.eliminar'])
  )

  // Proveedores: mostrar si puede editar o eliminar
  const mostrarAccionesProveedores = computed(() =>
    tieneAlgunPermiso(['proveedores.editar', 'proveedores.eliminar'])
  )

  // Sucursales: mostrar si puede editar o eliminar
  const mostrarAccionesSucursales = computed(() =>
    tieneAlgunPermiso(['sucursales.editar', 'sucursales.eliminar'])
  )

  // Bodegas: mostrar si puede editar o eliminar
  const mostrarAccionesBodegas = computed(() =>
    tieneAlgunPermiso(['bodegas.editar', 'bodegas.eliminar'])
  )

  // Cajas: mostrar si puede editar o eliminar
  const mostrarAccionesCajas = computed(() =>
    tieneAlgunPermiso(['cajas.editar', 'cajas.eliminar'])
  )

  // Vendedores: mostrar si puede editar o asignar
  const mostrarAccionesVendedores = computed(() =>
    tieneAlgunPermiso(['vendedores.editar', 'vendedores.asignar'])
  )

  // Usuarios: mostrar si puede editar o eliminar
  const mostrarAccionesUsuarios = computed(() =>
    tieneAlgunPermiso(['usuarios.editar', 'usuarios.eliminar'])
  )

  // Emisores: mostrar si puede gestionar (editar/eliminar)
  const mostrarAccionesEmisores = computed(() =>
    tienePermiso('emisores.gestionar')
  )

  // Compras: mostrar si puede confirmar o anular
  const mostrarAccionesCompras = computed(() =>
    tieneAlgunPermiso(['compras.confirmar', 'compras.anular'])
  )

  // Lotes: mostrar si puede enviar
  const mostrarAccionesLotes = computed(() =>
    tienePermiso('lotes.enviar')
  )

  // Historial/Facturas: mostrar si puede anular o reenviar
  const mostrarAccionesHistorial = computed(() =>
    tieneAlgunPermiso(['emision.anular', 'historial.reenviar'])
  )

  return {
    // Datos del usuario
    usuario,
    permisos,
    rol,

    // Información de sucursal asignada
    sucursalId,
    sucursalIds,
    sucursalNombre,
    tieneSucursalFija,
    requiereRestriccionSucursal,
    puedeAccederSucursal,

    // Funciones de verificación
    tienePermiso,
    tieneAlgunPermiso,
    tieneTodosLosPermisos,

    // Verificación de rol
    esSuperAdmin,
    esEmisorAdmin,
    esGerenteSucursal,
    esCajero,
    esContador,
    esAuditor,

    // Permisos específicos
    puedeVerDashboard,
    puedeVerEstadisticas,
    puedeCrearFactura,
    puedeCrearCreditoFiscal,
    puedeCrearFSE,
    puedeCrearNotaCredito,
    puedeCrearNotaDebito,
    puedeAnularDTE,
    puedeGuardarBorrador,
    puedeVerHistorial,
    puedeDescargarPDF,
    puedeDescargarJSON,
    puedeReenviarDTE,
    puedeVerClientes,
    puedeCrearCliente,
    puedeEditarCliente,
    puedeEliminarCliente,
    puedeVerProductos,
    puedeCrearProducto,
    puedeEditarProducto,
    puedeEliminarProducto,
    puedeVerCategorias,
    puedeCrearCategoria,
    puedeEditarCategoria,
    puedeEliminarCategoria,
    puedeVerMarcas,
    puedeCrearMarca,
    puedeEditarMarca,
    puedeEliminarMarca,
    puedeVerTiposGasto,
    puedeVerProveedores,
    puedeCrearProveedor,
    puedeEditarProveedor,
    puedeEliminarProveedor,
    puedeVerCompras,
    puedeCrearCompra,
    puedeConfirmarCompra,
    puedeAnularCompra,
    puedeVerReportes,
    puedeExportarReportes,
    puedeVerSucursales,
    puedeCrearSucursal,
    puedeEditarSucursal,
    puedeEliminarSucursal,
    puedeVerBodegas,
    puedeCrearBodega,
    puedeEditarBodega,
    puedeEliminarBodega,
    puedeVerCajas,
    puedeCrearCaja,
    puedeEditarCaja,
    puedeEliminarCaja,
    puedeVerVendedores,
    puedeCrearVendedor,
    puedeEditarVendedor,
    puedeAsignarVendedor,
    puedeVerUsuarios,
    puedeCrearUsuario,
    puedeEditarUsuario,
    puedeEliminarUsuario,
    puedeVerConfiguracion,
    puedeEditarConfiguracion,
    puedeVerEmisores,
    puedeGestionarEmisores,
    puedeVerMiSuscripcion,
    puedeVerSuscripciones,
    puedeCrearSuscripcion,
    puedeEditarSuscripcion,
    puedeEliminarSuscripcion,
    puedeVerInventario,
    puedeVerPendientes,
    puedeEnviarPendientes,
    puedeVerLotes,
    puedeCrearLote,
    puedeEnviarLote,
    puedeVerContingencia,
    puedeCrearContingencia,
    puedeActivarContingencia,
    puedeVerAuditoria,
    puedeEmitirDTE,
    puedeAccederConfiguracion,

    // Helpers para mostrar/ocultar columna de acciones
    mostrarAccionesClientes,
    mostrarAccionesProductos,
    mostrarAccionesCategorias,
    mostrarAccionesMarcas,
    mostrarAccionesProveedores,
    mostrarAccionesSucursales,
    mostrarAccionesBodegas,
    mostrarAccionesCajas,
    mostrarAccionesVendedores,
    mostrarAccionesUsuarios,
    mostrarAccionesEmisores,
    mostrarAccionesCompras,
    mostrarAccionesLotes,
    mostrarAccionesHistorial
  }
}

export default usePermisos
